import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getPaymentsForProject } from "../../services/paymentService";
import { formatCurrency } from "../../utils/formatCurrency";

const PaymentList = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const goBack = () => {
    navigate(projectId ? `/projects/${projectId}` : "/payments/overdue");
  };
  useEffect(() => {
    async function fetchPayments() {
      try {
        const data = await getPaymentsForProject(projectId);
        setPayments(data);
      } catch {
        setError("Failed to load payments.");
      } finally {
        setLoading(false);
      }
    }
    fetchPayments();
  }, [projectId]);

  const handleAddPayment = () => {
    navigate(`/payments/add/${projectId}`);
  };

  if (loading)
    return (
      <div className="p-6 text-center text-gray-600">Loading payments...</div>
    );

  return (
    <div className="p-0 md:p-6 space-y-6">
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        {/* LEFT */}
        <h1 className="text-2xl font-bold">💳 Payment List</h1>

        {/* RIGHT */}
        <div className="flex gap-3">
          <button
            onClick={handleAddPayment}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            + Add Payment
          </button>

          <button
            onClick={goBack}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
          >
            Back to Project
          </button>
        </div>
      </div>

      {error && <p className="text-red-500 text-center text-sm">{error}</p>}

      {/* EMPTY STATE */}
      {payments.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm p-6 text-center text-gray-500">
          No payments found for this project.
        </div>
      )}

      {/* ================= MOBILE CARDS ================= */}
      <div className="space-y-4 md:hidden">
        {payments.map((payment) => (
          <div
            key={payment.id}
            className="bg-white rounded-xl shadow-sm border p-4 space-y-2"
          >
            <div>
              <h2 className="font-semibold text-lg">{payment.title}</h2>
              <p className="text-sm text-gray-500">{payment.paymentDate}</p>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <Info label="Amount" value={formatCurrency(payment.amount)} />
              <Info label="Status" value={payment.status} />
            </div>

            <div className="pt-2">
              <button
                onClick={() =>
                  navigate(`/payments/edit/${payment.id}/${projectId}`)
                }
                className="text-blue-600 hover:underline text-sm"
              >
                ✏️ Edit Payment
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* ================= DESKTOP TABLE ================= */}
      {payments.length > 0 && (
        <div className="hidden md:block overflow-x-auto bg-white rounded-xl shadow-sm border">
          <table className="min-w-full table-auto text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-gray-700">Title</th>
                <th className="px-4 py-3 text-left text-gray-700">Amount</th>
                <th className="px-4 py-3 text-left text-gray-700">
                  Payment Date
                </th>
                <th className="px-4 py-3 text-left text-gray-700">Status</th>
                <th className="px-4 py-3 text-left text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((payment) => (
                <tr key={payment.id} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-3">{payment.title}</td>
                  <td className="px-4 py-3">
                    {formatCurrency(payment.amount)}
                  </td>
                  <td className="px-4 py-3">{payment.paymentDate}</td>
                  <td className="px-4 py-3">{payment.status}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() =>
                        navigate(`/payments/edit/${payment.id}/${projectId}`)
                      }
                      className="text-blue-600 hover:underline"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* ===================== SMALL COMPONENT ===================== */

const Info = ({ label, value }) => (
  <div className="bg-gray-50 rounded-lg p-2">
    <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
    <p className="font-medium text-gray-800">{value}</p>
  </div>
);

export default PaymentList;
